import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import { Picker, TextInput } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    confirm,
    Description,
    FieldGroup,
    Group,
    Label,
    toast,
} from '@gitbook/spine-react';

import { DocumentationLink } from '../../../../Components';
import { NavigationLink } from '../../../../navigation';
import { apiClient, apiQuery, useSiteContext, useUpgradeSite } from '../../../../state';

export const DEFAULT_VA_BACKEND = 'custom';
export const DEFAULT_FALLBACK_URL = '';

export type VisitorAuthState = {
    backend: api.SitePublishingAuth['backend'];
    fallbackURL: api.SitePublishingAuth['fallbackURL'];
};

export function VisibilityStateVisitorAuth(props: {
    state: VisitorAuthState;
    setState: React.Dispatch<React.SetStateAction<VisitorAuthState>>;
    integrationName: string | undefined;
    privateKey: string;
    onSave: () => void;
}) {
    const { onSave, state, setState, integrationName, privateKey } = props;
    const { site, organizationId } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesVisitorAuthentication);
    const { backend, fallbackURL } = state;
    const backendValue = backend ?? DEFAULT_VA_BACKEND;
    const fallbackURLValue = fallbackURL ?? DEFAULT_FALLBACK_URL;

    return (
        <div className="flex grow flex-col gap-6">
            <FieldGroup orientation="vertical">
                <Group>
                    <Label>Authentication backend</Label>
                    <Description>Choose a backend to authorize visitors.</Description>
                    {!integrationName ? (
                        <p className="max-w-96">
                            Install and{' '}
                            <NavigationLink
                                to={{
                                    screen: {
                                        type: 'integrations',
                                        site: site.id,
                                        category: api.IntegrationCategory.VisitorAuth,
                                    },
                                }}
                            >
                                enable an integration
                            </NavigationLink>{' '}
                            on the linked space to authenticate using Azure, Okta, and others.
                        </p>
                    ) : null}
                </Group>
                {integrationName ? (
                    <React.Suspense
                        fallback={
                            <BackendPicker
                                integrationLabel={integrationName}
                                value={backendValue}
                                onChange={(value) =>
                                    setState((state) => ({ ...state, backend: value }))
                                }
                                disabled={Boolean(upgrade)}
                            />
                        }
                    >
                        <IntegrationBackendPicker
                            integrationName={integrationName}
                            value={backendValue}
                            onChange={(value) =>
                                setState((state) => ({ ...state, backend: value }))
                            }
                            disabled={Boolean(upgrade)}
                        />
                    </React.Suspense>
                ) : (
                    <BackendPicker
                        integrationLabel={null}
                        value={backendValue}
                        onChange={(value) => setState((state) => ({ ...state, backend: value }))}
                        disabled={Boolean(upgrade)}
                    />
                )}
            </FieldGroup>

            {backendValue === 'custom' ? (
                <>
                    <TextInput
                        size="medium"
                        label="Private key"
                        labelDescription={
                            <>
                                Sign a JSON Web Token using the following key,{' '}
                                <DocumentationLink path="publishing-documentation/authenticated-access">
                                    Learn more.
                                </DocumentationLink>
                            </>
                        }
                        readOnly
                        copyable={!upgrade}
                        button={
                            !upgrade ? (
                                <Button
                                    label="Regenerate"
                                    size="small"
                                    kind="blank"
                                    leadingIcon={Icon.Reset}
                                    onPress={() => {
                                        void confirm({
                                            title: 'Regenerate private key?',
                                            description:
                                                'Regenerating the private key will require you to update your authentication backend. Are you sure you want to continue?',
                                            kind: 'danger',
                                            confirmLabel: 'Regenerate',
                                            onConfirm: async (_event, { preventClose }) => {
                                                await toast.catch(
                                                    async () => {
                                                        await apiClient.sitePublishingAuth.regenerateSitePublishingAuthById(
                                                            organizationId,
                                                            site.id
                                                        );
                                                        toast.success('Private key regenerated');
                                                    },
                                                    'Error while regenerating private key',
                                                    { onReject: preventClose }
                                                );
                                            },
                                        });
                                    }}
                                />
                            ) : null
                        }
                        value={upgrade ? '𐄂𐄂𐄂𐄂𐄂𐄂𐄂𐄂 𐄂𐄂𐄂𐄂 𐄂𐄂𐄂𐄂 𐄂𐄂𐄂𐄂 𐄂𐄂𐄂𐄂𐄂𐄂𐄂𐄂𐄂𐄂𐄂𐄂' : privateKey}
                        disabled={Boolean(upgrade)}
                    />
                    <TextInput
                        size="medium"
                        label="Fallback URL"
                        labelDescription={
                            <>
                                GitBook will redirect unauthenticated visitors here.{' '}
                                <DocumentationLink path="publishing-documentation/authenticated-access/setting-up-a-custom-backend#id-3.-configure-a-fallback-url">
                                    Learn more.
                                </DocumentationLink>
                            </>
                        }
                        value={fallbackURLValue}
                        onChangeText={(value) =>
                            setState((state) => ({ ...state, fallbackURL: value }))
                        }
                        placeholder="https://example.com/fallback"
                        onSubmit={onSave}
                        disabled={Boolean(upgrade)}
                    />
                </>
            ) : null}
        </div>
    );
}

function IntegrationBackendPicker(
    props: Omit<BackendPickerProps, 'integrationLabel'> & {
        integrationName: string;
    }
) {
    const { integrationName, ...pickerProps } = props;
    const { data: integration } = useSuspenseQuery(
        apiQuery('getIntegrationByName', { integrationName })
    );

    return <BackendPicker integrationLabel={integration.title} {...pickerProps} />;
}

type BackendPickerProps = {
    integrationLabel: string | null;
    disabled: boolean;
    value: api.SitePublishingAuth['backend'];
    onChange: (value: api.SitePublishingAuth['backend']) => void;
};

function BackendPicker(props: BackendPickerProps) {
    const { integrationLabel, disabled, value, onChange } = props;
    const options = React.useMemo(() => {
        const options: {
            value: api.SitePublishingAuth['backend'];
            label: string;
        }[] = [];
        options.push({
            value: 'custom',
            label: 'Custom',
        });
        if (integrationLabel) {
            options.push({
                value: 'integration',
                label: integrationLabel,
            });
        }
        return options;
    }, [integrationLabel]);
    return <Picker items={options} value={value} onValueChange={onChange} disabled={disabled} />;
}
